import { useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { useToast } from '@/components/ui/toastContext'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import { ReminderTaskModal } from '@/features/recruiter/components/ReminderTaskModal'
import type {
  AllCandidatesParams,
  AllCandidatesRow,
  AllCandidatesSortKey,
} from '@/features/recruiter/api/allCandidates.api'
import {
  useAllCandidatesFilterOptions,
  useAllCandidatesQuery,
  useToggleCandidateStar,
} from '@/features/recruiter/hooks/useAllCandidates'
import { useCandidateFilterOptions } from '@/features/recruiter/hooks/useCandidates'
import { getErrorMessage } from '@/utils/errors'
import { formatDate, formatMonthsAsYears, humanizeEnum } from '@/utils/format'

const EXPERIENCE_BUCKETS = [
  { value: '', label: 'Any' },
  { value: '0-0', label: 'Fresher (0)' },
  { value: '0-12', label: '0–1 yr' },
  { value: '12-36', label: '1–3 yrs' },
  { value: '36-60', label: '3–5 yrs' },
  { value: '60-120', label: '5–10 yrs' },
  { value: '120-', label: '10+ yrs' },
]

const NOTICE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '0', label: 'Immediate' },
  { value: '15', label: '≤ 15 days' },
  { value: '30', label: '≤ 30 days' },
  { value: '60', label: '≤ 60 days' },
  { value: '90', label: '≤ 90 days' },
]

const DEFAULT_DIRECTION: Record<AllCandidatesSortKey, 'asc' | 'desc'> = {
  name: 'asc',
  location: 'asc',
  notice: 'asc',
  experience: 'desc',
  completion: 'desc',
  updated: 'desc',
}

const EMPTY_FILTERS = {
  search: '',
  name: '',
  email: '',
  mobile: '',
  location: '',
  education: '',
  skills: '',
  company: '',
  designation: '',
  category: '',
  experience: '',
  notice: '',
  resume: '',
  starred: '',
}

type FilterState = typeof EMPTY_FILTERS

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

function toParams(f: FilterState): AllCandidatesParams {
  const [expMin, expMax] = f.experience ? f.experience.split('-') : ['', '']
  return {
    search: f.search.trim() || undefined,
    name: f.name.trim() || undefined,
    email: f.email.trim() || undefined,
    mobile: f.mobile.trim() || undefined,
    location: f.location.trim() || undefined,
    education: f.education.trim() || undefined,
    skills: f.skills.trim() || undefined,
    company: f.company.trim() || undefined,
    designation: f.designation.trim() || undefined,
    category: f.category || undefined,
    experience_min_months: expMin !== '' ? Number(expMin) : undefined,
    experience_max_months: expMax ? Number(expMax) : undefined,
    notice_period_max_days: f.notice !== '' ? Number(f.notice) : undefined,
    has_resume: f.resume === '' ? undefined : f.resume === '1' ? 1 : 0,
    starred: f.starred === '1' ? 1 : undefined,
  }
}

function TextFilter({
  value,
  onChange,
  placeholder,
  listId,
  suggestions,
  minWidth = 130,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  listId?: string
  suggestions?: string[]
  minWidth?: number
}) {
  return (
    <>
      <input
        type="text"
        className="form-control form-control-sm fs-12"
        style={{ minWidth }}
        placeholder={placeholder}
        value={value}
        list={listId}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Filter by ${placeholder}`}
      />
      {listId && suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </>
  )
}

/**
 * Multi-skill filter. Value is a comma-joined string (what the API's
 * `skills` param expects — candidates must match every skill). A native
 * datalist replaces the whole input on pick, so each skill is committed as
 * a chip instead: on suggestion pick, Enter, comma, or blur.
 */
function SkillsFilter({
  value,
  onChange,
  suggestions = [],
  listId,
}: {
  value: string
  onChange: (value: string) => void
  suggestions?: string[]
  listId: string
}) {
  const [draft, setDraft] = useState('')
  const skills = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  const add = (raw: string) => {
    const skill = raw.trim().replace(/,+$/, '').trim()
    setDraft('')
    if (!skill || skills.some((s) => s.toLowerCase() === skill.toLowerCase())) return
    onChange([...skills, skill].join(', '))
  }

  const remove = (skill: string) => onChange(skills.filter((s) => s !== skill).join(', '))

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      add(draft)
    } else if (e.key === 'Backspace' && draft === '') {
      const last = skills.at(-1)
      if (last) remove(last)
    }
  }

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const native = e.nativeEvent
    // Picking a datalist option fires a non-typing input event.
    const pickedSuggestion =
      !(native instanceof InputEvent) || native.inputType === 'insertReplacementText'
    if (pickedSuggestion && suggestions.includes(e.target.value)) {
      add(e.target.value)
    } else {
      setDraft(e.target.value)
    }
  }

  const available = suggestions.filter((s) => !skills.some((k) => k.toLowerCase() === s.toLowerCase()))

  return (
    <div
      className="form-control form-control-sm d-flex flex-wrap align-items-center gap-1 fs-12"
      style={{ minWidth: 180 }}
    >
      {skills.map((skill) => (
        <span key={skill} className="badge bg-primary-transparent text-primary d-flex align-items-center gap-1 fs-11">
          {skill}
          <button
            type="button"
            className="btn-close fs-10"
            aria-label={`Remove ${skill}`}
            onClick={() => remove(skill)}
          />
        </span>
      ))}
      <input
        type="text"
        className="border-0 flex-grow-1 fs-12 bg-transparent"
        style={{ outline: 'none', minWidth: 80 }}
        placeholder={skills.length === 0 ? 'e.g. PHP, MySQL' : 'Add skill…'}
        value={draft}
        list={listId}
        autoComplete="off"
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onBlur={() => add(draft)}
        aria-label="Filter by skills"
      />
      <datalist id={listId}>
        {available.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  )
}

function SelectFilter({
  value,
  onChange,
  options,
  label,
  minWidth = 110,
}: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  label: string
  minWidth?: number
}) {
  return (
    <select
      className="form-select form-select-sm fs-12"
      style={{ minWidth }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={`Filter by ${label}`}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

export function AllCandidatesPage() {
  // ?starred=1 (the dashboard's Starred Candidates tile) opens with that filter on.
  const [searchParams] = useSearchParams()
  const [filters, setFilters] = useState<FilterState>(() => ({
    ...EMPTY_FILTERS,
    starred: searchParams.get('starred') === '1' ? '1' : '',
  }))
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [sortKey, setSortKey] = useState<AllCandidatesSortKey>('updated')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [reminderTarget, setReminderTarget] = useState<{ id: number; name: string } | null>(null)

  const debouncedFilters = useDebouncedValue(filters, 400)
  const { data, isPending, isFetching } = useAllCandidatesQuery({
    ...toParams(debouncedFilters),
    sort: sortKey,
    direction,
    page,
    per_page: perPage,
  })
  const { data: options } = useAllCandidatesFilterOptions()
  const { data: suggestions } = useCandidateFilterOptions()
  const toggleStar = useToggleCandidateStar()
  const toast = useToast()
  const starPendingId = toggleStar.isPending ? toggleStar.variables?.studentProfileId : undefined

  const onToggleStar = (row: AllCandidatesRow) => {
    toggleStar.mutate(
      { studentProfileId: row.studentProfileId, starred: !row.isStarred },
      { onError: (err) => toast.error(getErrorMessage(err)) },
    )
  }

  const setFilter = (key: keyof FilterState) => (value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const onSortChange = (key: string) => {
    const next = key as AllCandidatesSortKey
    if (next === sortKey) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(next)
      setDirection(DEFAULT_DIRECTION[next])
    }
    setPage(1)
  }

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length

  const columns: DataTableColumn<AllCandidatesRow>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filter: <TextFilter value={filters.name} onChange={setFilter('name')} placeholder="Name" />,
      render: (row) => (
        <span className="fw-medium text-primary text-nowrap">{row.name ?? '—'}</span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      filter: <TextFilter value={filters.email} onChange={setFilter('email')} placeholder="Email" minWidth={160} />,
      render: (row) => <span className="fs-13 text-nowrap">{row.email ?? '—'}</span>,
    },
    {
      key: 'mobile',
      header: 'Mobile',
      filter: <TextFilter value={filters.mobile} onChange={setFilter('mobile')} placeholder="Mobile" minWidth={110} />,
      render: (row) =>
        row.mobile ?? <span className="text-muted fs-12" title="Hidden by candidate">Hidden</span>,
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      filter: (
        <TextFilter
          value={filters.location}
          onChange={setFilter('location')}
          placeholder="City / State"
          listId="all-candidates-locations"
          suggestions={suggestions?.locations}
        />
      ),
      render: (row) => (
        <span className="text-nowrap">
          {[row.currentCity, row.currentState].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'education',
      header: 'Qualification',
      filter: <TextFilter value={filters.education} onChange={setFilter('education')} placeholder="Degree" />,
      render: (row) => row.highestEducation ?? '—',
    },
    {
      key: 'skills',
      header: 'Skills',
      filter: (
        <SkillsFilter
          value={filters.skills}
          onChange={setFilter('skills')}
          listId="all-candidates-skills"
          suggestions={suggestions?.skills}
        />
      ),
      render: (row) =>
        row.skills.length === 0 ? (
          '—'
        ) : (
          <div className="d-flex flex-wrap gap-1" style={{ maxWidth: 260 }}>
            {row.skills.map((skill) => (
              <span key={skill} className="badge bg-primary-transparent text-primary fs-11">
                {skill}
              </span>
            ))}
            {row.skillCount > row.skills.length && (
              <span className="badge bg-light text-muted fs-11">+{row.skillCount - row.skills.length}</span>
            )}
          </div>
        ),
    },
    {
      key: 'experience',
      header: 'Experience',
      sortable: true,
      filter: (
        <SelectFilter
          value={filters.experience}
          onChange={setFilter('experience')}
          label="Experience"
          options={EXPERIENCE_BUCKETS}
        />
      ),
      render: (row) => (
        <span className="text-nowrap">
          {row.isFresher ? 'Fresher' : formatMonthsAsYears(row.totalExperienceMonths)}
        </span>
      ),
    },
    {
      key: 'company',
      header: 'Current Company',
      filter: (
        <TextFilter
          value={filters.company}
          onChange={setFilter('company')}
          placeholder="Company"
          listId="all-candidates-companies"
          suggestions={suggestions?.companies}
        />
      ),
      render: (row) => row.currentCompany ?? '—',
    },
    {
      key: 'designation',
      header: 'Designation',
      filter: (
        <TextFilter
          value={filters.designation}
          onChange={setFilter('designation')}
          placeholder="Designation"
          listId="all-candidates-designations"
          suggestions={suggestions?.designations}
        />
      ),
      render: (row) => row.currentDesignation ?? '—',
    },
    {
      key: 'category',
      header: 'Category',
      filter: (
        <SelectFilter
          value={filters.category}
          onChange={setFilter('category')}
          label="Category"
          minWidth={130}
          options={[
            { value: '', label: 'Any' },
            ...(options?.categories ?? []).map((c) => ({ value: c, label: humanizeEnum(c) })),
          ]}
        />
      ),
      render: (row) => (row.category ? humanizeEnum(row.category) : '—'),
    },
    {
      key: 'notice',
      header: 'Notice',
      sortable: true,
      filter: (
        <SelectFilter value={filters.notice} onChange={setFilter('notice')} label="Notice period" options={NOTICE_OPTIONS} />
      ),
      render: (row) => (row.noticePeriodDays == null ? '—' : `${row.noticePeriodDays} days`),
    },
    {
      key: 'completion',
      header: 'Profile',
      sortable: true,
      render: (row) => <span className="text-nowrap">{row.profileCompletionPercent}%</span>,
    },
    {
      key: 'resume',
      header: 'Resume',
      filter: (
        <SelectFilter
          value={filters.resume}
          onChange={setFilter('resume')}
          label="Resume"
          minWidth={80}
          options={[
            { value: '', label: 'Any' },
            { value: '1', label: 'Yes' },
            { value: '0', label: 'No' },
          ]}
        />
      ),
      render: (row) =>
        row.hasResume ? (
          <span className="badge bg-success-transparent text-success fs-11">Yes</span>
        ) : (
          <span className="badge bg-light text-muted fs-11">No</span>
        ),
    },
    {
      key: 'updated',
      header: 'Updated',
      sortable: true,
      render: (row) => <span className="fs-12 text-muted text-nowrap">{formatDate(row.updatedAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className={`btn btn-sm ${row.isStarred ? 'btn-warning-light' : 'btn-light'}`}
            title={row.isStarred ? 'Remove star' : 'Star candidate'}
            aria-label={row.isStarred ? `Remove star from ${row.name ?? 'candidate'}` : `Star ${row.name ?? 'candidate'}`}
            aria-pressed={row.isStarred}
            disabled={starPendingId === row.studentProfileId}
            onClick={(e) => {
              e.stopPropagation()
              onToggleStar(row)
            }}
          >
            <i className={`ti ${row.isStarred ? 'ti-star-filled text-warning' : 'ti-star'}`} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary-light"
            title="View profile"
            aria-label="View profile"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedProfileId(row.studentProfileId)
            }}
          >
            <i className="ti ti-eye" />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light"
            title="Set reminder"
            aria-label="Set reminder"
            onClick={(e) => {
              e.stopPropagation()
              setReminderTarget({ id: row.studentProfileId, name: row.name ?? 'Candidate' })
            }}
          >
            <i className="ti ti-bell" />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light"
            title="Notify candidate via email (coming soon)"
            aria-label="Notify candidate via email"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ti ti-mail" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-1">All Candidates</p>
          <p className="text-muted fs-13 mb-0">
            {data?.meta ? `${data.meta.total.toLocaleString()} candidate${data.meta.total === 1 ? '' : 's'} available` : 'Every candidate available to your organisation.'}
          </p>
        </div>
      </div>

      <div className="card custom-card">
        <div className="card-header d-flex align-items-center gap-2 flex-wrap">
          <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
            <span className="input-group-text bg-white">
              <i className="ti ti-search fs-13 text-muted" />
            </span>
            <input
              type="text"
              className="form-control fs-13"
              placeholder="Search name, email, city, skill, degree…"
              value={filters.search}
              onChange={(e) => setFilter('search')(e.target.value)}
              aria-label="Search all candidates"
            />
          </div>
          <button
            type="button"
            className={`btn btn-sm ${filters.starred === '1' ? 'btn-warning' : 'btn-light'}`}
            aria-pressed={filters.starred === '1'}
            onClick={() => setFilter('starred')(filters.starred === '1' ? '' : '1')}
          >
            <i className={`ti ${filters.starred === '1' ? 'ti-star-filled' : 'ti-star'} me-1`} />
            Starred only
          </button>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              onClick={() => {
                setFilters(EMPTY_FILTERS)
                setPage(1)
              }}
            >
              <i className="ti ti-filter-off me-1" />
              Clear filters ({activeFilterCount})
            </Button>
          )}
          {isFetching && !isPending && (
            <span className="spinner-border spinner-border-sm text-muted" role="status" aria-label="Loading" />
          )}
          <div className="ms-auto d-flex align-items-center gap-2 fs-13 text-muted">
            <span>Rows</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 80 }}
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value))
                setPage(1)
              }}
              aria-label="Rows per page"
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.rows ?? []}
          rowKey={(row) => row.studentProfileId}
          loading={isPending}
          emptyMessage="No candidates match these filters."
          sortKey={sortKey}
          sortDirection={direction}
          onSortChange={onSortChange}
          onRowClick={(row) => setSelectedProfileId(row.studentProfileId)}
          density="compact"
          pagination={data?.meta}
          onPageChange={setPage}
        />
      </div>

      <CandidateDetailDrawer
        show={selectedProfileId != null}
        onClose={() => setSelectedProfileId(null)}
        studentProfileId={selectedProfileId ?? undefined}
      />

      <ReminderTaskModal
        show={reminderTarget != null}
        onClose={() => setReminderTarget(null)}
        studentProfileId={reminderTarget?.id ?? null}
        candidateName={reminderTarget?.name}
      />
    </>
  )
}
